// src/profile/usePublicProfile.ts
//
// Realtime read of a user's PUBLIC profile fields. Used by the
// profile page (their own view OR another user's view).
//
// Reads from /users/{uid}. Security rules permit any authenticated
// user to read the public subset of fields; private fields
// (travelProfile, GDPR, prefs) remain owner-only. Since this hook
// reads the WHOLE doc, the rule's read-allowance must include
// authenticated reads — see firestore.rules where this is gated on
// `request.auth != null`.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { PublicProfileFields } from './types';

export interface UsePublicProfileResult {
  profile: PublicProfileFields | null;
  loading: boolean;
  error: string | null;
}

export function usePublicProfile(uid: string | null): UsePublicProfileResult {
  const [profile, setProfile] = useState<PublicProfileFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          setLoading(false);
          return;
        }
        const data = snap.data() as any;
        // Project ONLY the public fields client-side. The auth
        // store's UserProfile shape includes private fields too;
        // keeping this projection minimal makes downstream rendering
        // explicit about what's public.
        setProfile({
          username: data.username,
          username_lower: data.username_lower,
          display_name: data.display_name || data.displayName,
          photo_url: data.photo_url || data.photoURL,
          is_account_private: !!data.is_account_private,
          followers_count: data.followers_count ?? 0,
          following_count: data.following_count ?? 0,
          posts_count: data.posts_count ?? 0,
        });
        setLoading(false);
      },
      (err) => {
        console.warn('usePublicProfile listener error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  return { profile, loading, error };
}
